import { calendarConfig } from "../constants/calendarConfig";

export const getMonday = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
};

export const addDays = (d, n) => {
  const date = new Date(d);
  date.setDate(date.getDate() + n);
  return date;
};

export const formatDateShort = (d) => {
  return `${d.getDate()} ${calendarConfig.MONTH_LABELS[d.getMonth()].substring(0, 3)}`;
};

export const formatDateISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

export const isSameDay = (a, b) => {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

export const parseDateTime = (s) => {
  return new Date(s);
};

const zonedPartsFormatter = (timeZone) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });

export const getBrowserTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

export const getDateTimePartsInTimeZone = (value, timeZone) => {
  const parts = zonedPartsFormatter(timeZone).formatToParts(new Date(value));
  const values = Object.fromEntries(
    parts.filter(({ type }) => type !== "literal").map(({ type, value }) => [type, value]),
  );

  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    hour: Number(values.hour),
    minute: Number(values.minute),
    second: Number(values.second),
  };
};

export const formatDateInTimeZone = (value, timeZone) => {
  const { year, month, day } = getDateTimePartsInTimeZone(value, timeZone);
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

// Returns a local Date whose fields represent the calendar date in `timeZone`.
// It is intended for date-only calendar navigation, not as an instant in time.
export const getDateInTimeZone = (value, timeZone) => {
  const { year, month, day } = getDateTimePartsInTimeZone(value, timeZone);
  return new Date(year, month - 1, day);
};

// Converts wall-clock form values in an IANA timezone into an unambiguous UTC ISO value.
export const zonedDateTimeToIso = (dateValue, timeValue, timeZone) => {
  const [year, month, day] = dateValue.split("-").map(Number);
  const [hour, minute] = timeValue.split(":").map(Number);
  const desiredWallTime = Date.UTC(year, month - 1, day, hour, minute, 0);
  let utcGuess = desiredWallTime;

  // Timezone offsets can change around DST, so refine the guess until its
  // formatted wall-clock parts match the requested values.
  for (let i = 0; i < 3; i += 1) {
    const parts = getDateTimePartsInTimeZone(utcGuess, timeZone);
    const representedWallTime = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
    );
    const correction = desiredWallTime - representedWallTime;
    utcGuess += correction;
    if (correction === 0) break;
  }

  return new Date(utcGuess).toISOString();
};
